import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export type TerritoryRuleOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'in_list';

export interface TerritoryRule {
  field: string;
  operator: TerritoryRuleOperator;
  value: string;
}

export interface Territory {
  id: string;
  name: string;
  description?: string;
  assigneeId?: string;
  assigneeName?: string;
  rules: TerritoryRule[];
  isActive: boolean;
  recordCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TerritoriesResponse {
  territories: Territory[];
  total: number;
}

export interface TerritoryMatchResult {
  matched: boolean;
  territoryId?: string;
  territoryName?: string;
  assigneeName?: string;
}

// --- Hooks ---

export function useTerritories() {
  return useQuery({
    queryKey: ['territories'],
    queryFn: () => fetchJson<TerritoriesResponse>('/api/territories'),
  });
}

export function useCreateTerritory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Territory>) =>
      postJson<Territory>('/api/territories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['territories'] });
    },
  });
}

export function useUpdateTerritory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Territory> }) =>
      putJson<Territory>(`/api/territories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['territories'] });
    },
  });
}

export function useDeleteTerritory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/territories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['territories'] });
    },
  });
}

export function useAutoAssign() {
  return useMutation({
    mutationFn: (record: Record<string, unknown>) =>
      postJson<TerritoryMatchResult>('/api/territories/auto-assign', record),
  });
}
